import { useState, useEffect } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { imagepathReturn } from "../../config/formatters.ts";
import "../../styles/scss/Form.scss";

interface Paragraph {
  award_title: string;
  image: string;
  preview: string;
  file: File | null;
}

const emptyParagraph = (): Paragraph => ({
  award_title: "",
  image: "",
  preview: "",
  file: null,
});

export default function AchievementCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      category: "",
      subtitle: "",
      date: "",
      location: "",
      tagline: "",
      description: "",
      descriptions: [emptyParagraph()] as Paragraph[],
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      date: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();

      formData.append("title", values.title);
      formData.append("category", values.category || "");
      formData.append("subtitle", values.subtitle || "");
      formData.append("date", values.date || "");
      formData.append("location", values.location || "");
      formData.append("tagline", values.tagline || "");
      formData.append("description", values.description || "");
      formData.append("sort_order", values.sort_order);
      formData.append("status", values.isActive === "Active" ? "true" : "false");

      const descriptions: {
        award_title: string;
        image: string | null;
      }[] = [];

      values.descriptions.forEach((paragraph) => {
        if (
          paragraph.award_title.trim() === "" &&
          !paragraph.file &&
          !paragraph.image
        ) {
          return;
        }

        const index = descriptions.length;
        descriptions.push({
          award_title: paragraph.award_title,
          image: paragraph.file ? null : paragraph.image || null,
        });

        if (paragraph.file) {
          formData.append("paragraph_images[]", paragraph.file);
          formData.append("paragraph_image_indexes[]", String(index));
        }
      });

      formData.append("descriptions", JSON.stringify(descriptions));

      if (isEdit) {
        formData.append("_method", "PUT");
      }

      try {
        let response;
        if (isEdit) {
          response = await request.post("achievements/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("achievements/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(
            isEdit
              ? "Achievement updated successfully!"
              : "Achievement created successfully!",
          );
          navigate("/admin/achievements");
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`achievements/${id}`);
          const item = response.data.data;

          const descriptions: Paragraph[] = (item.descriptions || []).map(
            (paragraph: any) => {
              if (typeof paragraph === "string") {
                return {
                  award_title: "",
                  image: "",
                  preview: "",
                  file: null,
                };
              }

              return {
                award_title: paragraph.award_title || "",
                image: paragraph.image || "",
                preview: paragraph.image ? imagepathReturn(paragraph.image) : "",
                file: null,
              };
            },
          );

          formik.setValues({
            title: item.title || "",
            category: item.category || "",
            subtitle: item.subtitle || "",
            date: item.date || "",
            location: item.location || "",
            tagline: item.tagline || "",
            description: item.description || "",
            descriptions: descriptions.length
              ? descriptions
              : [emptyParagraph()],
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addParagraph = () => {
    formik.setFieldValue("descriptions", [
      ...formik.values.descriptions,
      emptyParagraph(),
    ]);
  };

  const removeParagraph = (index: number) => {
    const next = formik.values.descriptions.filter((_, i) => i !== index);
    formik.setFieldValue(
      "descriptions",
      next.length ? next : [emptyParagraph()],
    );
  };

  const updateParagraph = (index: number, updates: Partial<Paragraph>) => {
    const next = formik.values.descriptions.map((paragraph, i) =>
      i === index ? { ...paragraph, ...updates } : paragraph,
    );
    formik.setFieldValue("descriptions", next);
  };

  const handleParagraphImage = (index: number, file?: File) => {
    if (!file) return;

    updateParagraph(index, {
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const removeParagraphImage = (index: number) => {
    updateParagraph(index, {
      file: null,
      image: "",
      preview: "",
    });
  };

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Achievement" : "Create New Achievement"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/achievements"),
              }}
              primaryButton={{
                label: isEdit ? "Update Achievement" : "Create Achievement",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="title"
                    label="Title"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="category"
                    label="Category"
                    type="text"
                    formik={formik}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="subtitle"
                    label="Subtitle"
                    type="text"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="location"
                    label="Location"
                    type="text"
                    formik={formik}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--3" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="date"
                    label="Date"
                    type="text"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="sort_order"
                    label="Sort Order"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box sx={{ marginTop: "16px" }}>
                <TextField
                  name="tagline"
                  label="Tagline"
                  type="text"
                  multiline
                  minRows={2}
                  formik={formik}
                />
              </Box>

              <Box sx={{ marginTop: "16px" }}>
                <TextField
                  name="description"
                  label="Description"
                  type="text"
                  multiline
                  minRows={4}
                  formik={formik}
                />
              </Box>

              <Box sx={{ marginTop: "24px" }}>
                <Typography sx={{ fontWeight: 600, color: "#1a1f3a", mb: 1 }}>
                  Awards &amp; Images
                </Typography>
                {formik.values.descriptions.map((paragraph, index) => (
                  <Stack
                    key={`description-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        name={`descriptions.${index}.award_title`}
                        label={`Award Title ${index + 1} (separate multiple titles with commas)`}
                        type="text"
                        formik={formik}
                      />

                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ mt: 1 }}
                      >
                        {paragraph.preview ? (
                          <Box
                            component="img"
                            src={paragraph.preview}
                            sx={{
                              width: 90,
                              height: 70,
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        ) : null}

                        <Box
                          component="label"
                          htmlFor={`paragraph-image-${index}`}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            border: "1px dashed #94a3b8",
                            borderRadius: "8px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                          }}
                        >
                          <span className="material-symbols-outlined">
                            image
                          </span>
                          {paragraph.preview ? "Replace image" : "Add image"}
                        </Box>

                        <input
                          id={`paragraph-image-${index}`}
                          type="file"
                          accept="image/*"
                          onChange={(event) => {
                            handleParagraphImage(
                              index,
                              event.target.files?.[0],
                            );
                            event.target.value = "";
                          }}
                          style={{ display: "none" }}
                        />

                        {paragraph.preview ? (
                          <Box
                            component="button"
                            type="button"
                            onClick={() => removeParagraphImage(index)}
                            sx={{
                              border: "none",
                              background: "transparent",
                              color: "#dc2626",
                              cursor: "pointer",
                              fontSize: "0.85rem",
                              fontWeight: 600,
                            }}
                          >
                            Remove image
                          </Box>
                        ) : null}
                      </Stack>
                    </Box>

                    <IconButton
                      color="error"
                      onClick={() => removeParagraph(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Box
                  component="button"
                  type="button"
                  onClick={addParagraph}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    border: "none",
                    background: "transparent",
                    color: "#667eea",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  <AddIcon fontSize="small" /> Add award image
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
